import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PaymentResult() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const invoiceId = searchParams.get("invoice_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      // Cancel page
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/stripe/checkout/status/${sessionId}`);
      setStatus(res.data);

      if (res.data.payment_status === "paid" || res.data.status === "expired" || res.data.processing) {
        setLoading(false);
        return;
      }

      // Keep polling if still pending
      if (attempts < 10) {
        setTimeout(() => setAttempts(prev => prev + 1), 2000);
      } else {
        setLoading(false);
      }
    } catch (e) {
      console.error("Status check failed:", e);
      setLoading(false);
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (sessionId) {
      pollStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId, attempts, pollStatus]);

  // Cancel page
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-cancel">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-14 h-14 text-slate-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-slate-700 mb-2">Payment Cancelled</h2>
            <p className="text-sm text-slate-500 mb-4">No charges were made to your account.</p>
            {invoiceId && (
              <Link to={`/pay/${invoiceId}`} className="text-[#0F4C81] text-sm font-medium hover:underline">
                Return to Invoice
              </Link>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading / polling
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-10 h-10 animate-spin text-[#0F4C81] mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-700 mb-1">Confirming Payment...</h2>
            <p className="text-sm text-slate-500">Please wait while we verify your payment.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPaid = status?.payment_status === "paid";
  const isProcessing = !isPaid && !!status?.processing;
  const amountPaid = status?.amount_total ? (status.amount_total / 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="payment-result">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {isPaid ? (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2" data-testid="payment-success-title">Payment Successful!</h2>
              <p className="text-slate-500 mb-1">
                ${amountPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })} has been received.
              </p>
              <p className="text-sm text-slate-400">Thank you! A confirmation will be sent to your email.</p>
            </>
          ) : isProcessing ? (
            <>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-[#0F4C81]" />
              </div>
              <h2 className="text-xl font-bold text-slate-800 mb-2" data-testid="payment-processing-title">Bank Payment Started</h2>
              <p className="text-slate-500 mb-1">
                Your bank payment of ${amountPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })} is on its way.
              </p>
              <p className="text-sm text-slate-400">Bank payments take up to 4 business days to clear. We will email your receipt when it lands. Nothing else for you to do.</p>
            </>
          ) : (
            <>
              <XCircle className="w-14 h-14 text-red-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-slate-700 mb-2">Payment Issue</h2>
              <p className="text-sm text-slate-500">
                {status?.status === "expired"
                  ? "This payment session has expired. Please request a new invoice link."
                  : "We couldn't confirm your payment. Please contact us if you were charged."}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
